import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABELS } from '@/lib/roles';

export default function InvitationAcceptPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', invite: null });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/invitations/${token}`);
        setState({ loading: false, error: '', invite: data });
      } catch (err) {
        setState({ loading: false, error: err?.response?.data?.detail || 'Invitación inválida', invite: null });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Mínimo 8 caracteres');
    if (password !== confirm) return toast.error('Las contraseñas no coinciden');
    setSubmitting(true);
    try {
      await api.post(`/public/invitations/${token}/accept`, { password });
      setDone(true);
      toast.success('Cuenta activada');
      setTimeout(() => nav('/login'), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden noise-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-[hsl(var(--chart-2))]/15" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-[10px] bg-primary text-primary-foreground grid place-items-center font-display font-bold text-lg">Z</div>
            <span className="font-display text-xl font-semibold">Zunoid CRM</span>
          </div>
          <div className="max-w-md">
            <h2 className="font-display text-3xl xl:text-4xl font-semibold leading-tight">Activa tu cuenta</h2>
            <p className="text-muted-foreground mt-3">Define una contraseña segura para empezar a usar Zunoid CRM con tu equipo.</p>
          </div>
          <div className="text-xs text-muted-foreground">© {new Date().getFullYear()} Zunoid</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div data-testid="invitation-card" className="w-full max-w-md surface p-8">
          {state.loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando invitación…</div>
          ) : state.error ? (
            <div className="text-center">
              <AlertTriangle className="h-10 w-10 mx-auto text-[hsl(var(--danger))]" />
              <h2 className="font-display text-xl font-semibold mt-3">Invitación no válida</h2>
              <p className="text-sm text-muted-foreground mt-1">{state.error}</p>
              <Link to="/login" className="btn-primary mt-6 inline-flex">Ir al login</Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto text-[hsl(var(--success))]" />
              <h2 className="font-display text-xl font-semibold mt-3">¡Cuenta activada!</h2>
              <p className="text-sm text-muted-foreground mt-1">Te estamos llevando al login…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Bienvenido, {state.invite.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">Has sido invitado a <b>{state.invite.tenant.name}</b> como <b>{ROLE_LABELS[state.invite.role]}</b>.</p>
                <p className="text-xs text-muted-foreground mt-1">Email: <span className="font-mono">{state.invite.email}</span></p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Nueva contraseña (mín 8)</label>
                <input data-testid="invite-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input mt-1" autoFocus />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Confirmar contraseña</label>
                <input data-testid="invite-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input mt-1" />
              </div>
              <button data-testid="invite-submit" disabled={submitting} className="btn-primary w-full">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Activar cuenta</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
